/**
 * Activity and the idle timeout. User input (and media playing) tells PRISM the person is active, at most
 * once a minute; background polling never does, so an idle open tab really times out. With a timeout set
 * in Administration → Access, a warning appears one minute before, and at the timeout the app signs out
 * with "Signed out after N minutes of inactivity" on the sign-in page.
 *
 * Another tab (or a change such as an upload) keeps the same session active, so before warning and before
 * signing out the app asks the server how long the session has really been idle.
 *
 * 1.2.4: a background tab or a sleeping computer slows or stops timers, so the warning could not appear and the app
 * stayed on screen past the deadline. Now the check also runs as soon as the tab is shown again or the window gets
 * focus, and while the warning shows the tab's title says so.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { Modal } from './Modal';
import { endSession, idleMessage, setIdleClock } from '../api/session-end';

const SEND_EVERY_MS = 60_000;
const TICK_MS = 5_000;
const WARN_BEFORE_MS = 60_000;
const WARNING_TITLE = 'Signing out soon – PRISM';

export function IdleGuard({ idleMinutes }: { idleMinutes: number }) {
  const qc = useQueryClient();
  const [warnUntil, setWarnUntil] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const state = useRef({ lastSent: 0, pending: true, pendingAt: Date.now(), base: Date.now(), checking: false, signingOut: false });

  useEffect(() => {
    const s = state.current;
    const send = () => {
      s.lastSent = Date.now();
      s.pending = false;
      api('/api/v1/me/activity', { method: 'POST', json: {} }).then(() => { s.base = Math.max(s.base, s.lastSent); }).catch(() => undefined);
    };
    const active = () => {
      if (s.signingOut) return;
      if (Date.now() - s.lastSent >= SEND_EVERY_MS) send();
      else { s.pending = true; s.pendingAt = Date.now(); }
    };
    // Media playing counts too: media events do not bubble, but a capturing listener on the document hears them.
    const playing = (e: Event) => {
      const m = e.target as HTMLMediaElement | null;
      if (m && !m.paused) active();
    };
    const signOut = async (minutes: number) => {
      s.signingOut = true;
      await api('/api/v1/auth/logout', { method: 'POST', json: {} }).catch(() => undefined);
      endSession(qc, idleMessage(minutes));
    };
    /** Learns how long the server has seen this session idle (another tab may have kept it active). */
    const check = async () => {
      if (s.checking) return;
      s.checking = true;
      try {
        const r = await api<{ idleSeconds: number; idleMinutes: number }>('/api/v1/me/session');
        s.base = Date.now() - r.idleSeconds * 1000;
      } catch {
        // Already signed out elsewhere: the next query reports it.
      } finally {
        s.checking = false;
      }
    };
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'pointermove'] as const;
    for (const e of events) window.addEventListener(e, active, { passive: true, capture: true });
    document.addEventListener('timeupdate', playing, true);
    const evaluate = async () => {
      if (s.signingOut) return;
      // Activity not sent yet is only sent while it is recent: after a sleep it would tell the server the person is
      // active now (1.2.4).
      if (s.pending && Date.now() - s.pendingAt >= SEND_EVERY_MS) s.pending = false;
      if (s.pending && Date.now() - s.lastSent >= SEND_EVERY_MS) send();
      setNow(Date.now());
      if (idleMinutes <= 0) return;
      const timeout = idleMinutes * 60_000;
      const idleFor = Date.now() - s.base;
      if (idleFor < timeout - WARN_BEFORE_MS) { setWarnUntil(null); return; }
      await check();
      const deadline = s.base + timeout;
      if (Date.now() >= deadline - 1000) { setWarnUntil(null); await signOut(idleMinutes); return; }
      setWarnUntil(Date.now() >= deadline - WARN_BEFORE_MS ? deadline : null);
    };
    const tick = window.setInterval(() => void evaluate(), TICK_MS);
    // Back to a tab that was hidden or a computer that slept: check at once rather than at the next (late) tick.
    const shown = () => { if (document.visibilityState === 'visible') void evaluate(); };
    const focused = () => void evaluate();
    document.addEventListener('visibilitychange', shown);
    window.addEventListener('focus', focused);
    setIdleClock({ base: () => s.base, minutes: idleMinutes });
    return () => {
      for (const e of events) window.removeEventListener(e, active, { capture: true });
      document.removeEventListener('timeupdate', playing, true);
      document.removeEventListener('visibilitychange', shown);
      window.removeEventListener('focus', focused);
      window.clearInterval(tick);
      setIdleClock(null);
    };
  }, [idleMinutes, qc]);

  // The warning in the tab's title too, for a tab in the background (1.2.4).
  const warning = warnUntil !== null;
  useEffect(() => {
    if (!warning) return;
    const title = document.title;
    document.title = WARNING_TITLE;
    return () => { document.title = title; };
  }, [warning]);

  if (warnUntil === null) return null;
  const seconds = Math.max(0, Math.ceil((warnUntil - now) / 1000));
  const stay = () => {
    const s = state.current;
    s.lastSent = Date.now();
    s.pending = false;
    void api('/api/v1/me/activity', { method: 'POST', json: {} }).then(() => { s.base = Date.now(); setWarnUntil(null); }).catch(() => undefined);
  };
  return (
    <Modal title="Are you still there?" onClose={stay}
      actions={<button className="btn primary" onClick={stay} data-testid="idle-stay">Stay signed in</button>}>
      <p data-testid="idle-warning">You will be signed out in {seconds} second{seconds === 1 ? '' : 's'} because there has been no activity for {idleMinutes - 1} minute{idleMinutes - 1 === 1 ? '' : 's'}.</p>
    </Modal>
  );
}
